import React from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const TEAM = [
  {
    name: "Aymeric Gairaud",
    title: "Founder & CEO",
    img: "/lovable-uploads/32353198-9cc5-4822-9d6a-eb92b70eb65f.png",
    desc: "Aymeric worked in the Traffic Control industry for over 10 years in various positions and companies before founding USA Traffic Control in 2021."
  },
  {
    name: "Chetan Mudgal",
    title: "VP of Operations",
    img: "/lovable-uploads/b5ba1599-f405-4fe7-8356-9f8e2155497e.png",
    desc: "Before joining USA Traffic Control Chetan worked in automotive manufacturing. He has both an MBA and a mechanical engineering degree."
  },
  {
    name: "Lauren Shannon",
    title: "Billing & Admin",
    img: "/lovable-uploads/d63ba202-1535-4efc-80c5-74fcb42e40da.png",
    desc: "Lauren is one of USA Traffic Control's newest team members, she brings a passion for problem solving and superb attention to detail to our growing team."
  },
  {
    name: "Steve Bolman",
    title: "TCP Designer",
    img: "/lovable-uploads/2d7819ca-191a-4b8e-8050-b6781305249c.png",
    desc: "Steve has worked in Traffic Engineering for years and brings a wealth of knowledge and expertise to our Traffic Control Plans Division."
  }
];

const Section = ({ bg, title, children, className }) => (
  <section className={`${bg} py-16 md:py-20`}>
    <div className={`container mx-auto px-4 max-w-5xl ${className || ""}`}>
      <h2 className="text-3xl font-bold mb-4 text-left text-gray-900">{title}</h2>
      <div className="text-gray-700 text-lg space-y-16">{children}</div>
    </div>
  </section>
);

const About = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>About USA Traffic Control | Our Story & Team</title>
        <meta name="description" content="Learn about USA Traffic Control's history, our dedicated team, and our commitment to providing innovative traffic management solutions since 2021." />
        <meta name="keywords" content="about USA traffic control, traffic management team, traffic control company history" />
        <link rel="canonical" href="https://usatrafficcontrol.com/about" />
      </Helmet>
      
      <div className="relative w-full h-[600px] overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-end pb-16 z-10">
          <div className="w-full flex">
            <div className="w-[90%] md:w-[85%] lg:w-[80%] xl:w-[75%] text-right p-4 md:p-6 bg-orange-400">
              <div className="ml-auto w-[55%] text-left">
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.8rem] leading-none font-bold text-white">
                  About Us
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.8rem] leading-none font-bold text-white mt-2">
                  Safety First, Always
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute w-full h-full">
          <img
            src="/lovable-uploads/0b469f3d-856f-46dd-bdbd-a72675f5f325.png"
            alt="About USA Traffic Control"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 0%' }}
          />
        </div>
      </div>

      <main className="flex-grow">
        <Section bg="bg-gray-100" title="USA Traffic Control Key Principles">
          <ul className="list-disc pl-5 space-y-2 text-lg">
            <li>Provide our clients with innovative and efficient Traffic Control Solutions</li>
            <li>Build lasting relationships with industry leading companies</li>
            <li>Deliver exceptional quality at competitive prices</li>
            <li>Safety is always the #1 priority</li>
          </ul>
        </Section>

        <Section bg="white" title="Industry Background & History">
          <div className="space-y-8">
            <p>
              USA Traffic Control was founded in 2021 with the goal of combining innovative technology with an old-school work ethic and attention to detail in order to provide cost effective and robust Traffic Control Services to clients nationwide.
            </p>
            <p>
              We are leading the Traffic Control Industry in innovation. We actively find areas that can be disrupted and made more efficient. We have been early adopters of safety enhancing practices like automated-flagger-devices and personal worker beacons as well as designing and building our own products and solutions to address areas of the industry that had opportunity for improvement.
            </p>
            <p>
              With years of experience and thousands of successful projects completed, we have the experience, knowledge and capabilities to provide Traffic Control Services to projects of any size and duration.
            </p>
          </div>
        </Section>

        <Section bg="bg-gray-100" title="Get to know our team" className="pb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8 mt-8">
            {TEAM.map((member) => (
              <Card
                key={member.name}
                className="border-none bg-white hover:shadow-lg transition-shadow h-full overflow-hidden"
                style={{
                  fontSize: "0.94rem"
                }}
              >
                <div className="h-36 md:h-40 lg:h-44 xl:h-40 w-full overflow-hidden">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'top center' }}
                    loading="eager"
                  />
                </div>
                <CardHeader className="p-4 pb-2 xl:p-4 xl:pb-2">
                  <CardTitle className="text-gray-800 text-left leading-tight -mb-1 text-[1.07rem] md:text-lg">
                    {member.name}
                  </CardTitle>
                  <p className="text-orange-500 font-semibold text-xs md:text-sm mt-0.5 mb-2">
                    {member.title}
                  </p>
                </CardHeader>
                <CardContent className="px-4 pt-0 pb-3 md:pb-4 mt-0 text-gray-600 text-left" style={{ fontSize: "0.99rem" }}>
                  {member.desc}
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
